/**
 * GeneratedCodeService
 * Service for generated code ModelView endpoints (cache-first pattern)
 * Route: api/codes/generated
 * Controller: GeneratedCodesController.cs
 */
import { BaseService } from '../base/BaseService';
/**
 * GeneratedCodeService
 * Handles retrieval and filtering of generated code records
 */
export class GeneratedCodeService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? GeneratedCodeService.DEFAULT_SERVICE_PATH,
            basePath: GeneratedCodeService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== READ OPERATIONS ====================
    /**
     * Get a single generated code by ID (ModelView)
     * GET api/codes/generated/view/{id}
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        if (!response.data) {
            throw new Error(response.message || 'Generated code not found');
        }
        return response.data;
    }
    /**
     * Get generated code view with full response
     */
    getViewRaw(id) {
        const endpoint = this.getEndpoint('view-by-id', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all generated codes (ModelViews) - Limited to 1000 for performance
     * GET api/codes/generated/view
     */
    async getAllViews() {
        const response = await this.getAllViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all views with full response
     */
    getAllViewsRaw() {
        const endpoint = this.getEndpoint('view', 'view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get generated codes by user (ModelViews)
     * GET api/codes/generated/user/{userId}/view
     */
    async getViewsByUser(userId) {
        const response = await this.getViewsByUserRaw(userId);
        return response.data ?? [];
    }
    /**
     * Get views by user with full response
     */
    getViewsByUserRaw(userId) {
        const endpoint = this.getEndpoint('views-by-user', `user/${encodeURIComponent(userId)}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get generated codes by domain and record (ModelViews)
     * GET api/codes/generated/domain/{domain}/{recordId}/view
     */
    async getViewsByDomain(domain, recordId) {
        const response = await this.getViewsByDomainRaw(domain, recordId);
        return response.data ?? [];
    }
    /**
     * Get views by domain with full response
     */
    getViewsByDomainRaw(domain, recordId) {
        const endpoint = this.getEndpoint('views-by-domain', `domain/${encodeURIComponent(domain)}/${encodeURIComponent(recordId)}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get generated codes by code type (ModelViews)
     * GET api/codes/generated/type/{codeType}/view
     */
    async getViewsByCodeType(codeType) {
        const response = await this.getViewsByCodeTypeRaw(codeType);
        return response.data ?? [];
    }
    /**
     * Get views by code type with full response
     */
    getViewsByCodeTypeRaw(codeType) {
        const endpoint = this.getEndpoint('views-by-code-type', `type/${encodeURIComponent(codeType)}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== FILTERED LIST OPERATIONS ====================
    /**
     * Get filtered generated codes with pagination (ModelViews)
     * POST api/codes/generated/list-view
     */
    async getFilteredViews(filter) {
        const response = await this.getFilteredViewsRaw(filter);
        if (!response.data) {
            throw new Error(response.message || 'Failed to get filtered views');
        }
        return response.data;
    }
    /**
     * Get filtered views with full response
     */
    getFilteredViewsRaw(filter) {
        const endpoint = this.getEndpoint('list-view', 'list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter);
    }
    /**
     * Get current user's generated codes with filtering (ModelViews)
     * POST api/codes/generated/my-list-view
     */
    async getMyFilteredViews(filter) {
        const response = await this.getMyFilteredViewsRaw(filter);
        if (!response.data) {
            throw new Error(response.message || 'Failed to get my filtered views');
        }
        return response.data;
    }
    /**
     * Get my filtered views with full response
     */
    getMyFilteredViewsRaw(filter) {
        const endpoint = this.getEndpoint('my-list-view', 'my-list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter);
    }
}
// Service path must match the nginx /code-generators/ location block.
GeneratedCodeService.DEFAULT_SERVICE_PATH = 'code-generators';
GeneratedCodeService.BASE_PATH = 'api/codes/generated';
//# sourceMappingURL=GeneratedCodeService.js.map